import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";

const FADE = 1.1; // seconds of crossfade either side of a loop seam
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23f)' opacity='0.5'/%3E%3C/svg%3E\")";

/**
 * Full-bleed dark cinematic band. The page around it stays light — the band is a
 * deliberate interruption, not a theme change.
 *
 * Craft notes, all deliberate:
 *  - WebM first, H.264 fallback, both faststart and silent.
 *  - Poster paints instantly and stays underneath; the video fades in over 1.2s
 *    on `canplay` so there is never a hard swap.
 *  - Every clip in this set has a visible loop seam, so two elements run the same
 *    source half a cycle apart and cross-fade through each seam.
 *  - playbackRate is slowed (0.6, hero 0.5) — the single biggest lever on feel.
 *  - Grain + vignette above the video, scrim above those, content above all.
 *  - Reduced motion: poster only. < 768px: poster with a slow Ken Burns drift so
 *    the band still feels alive without the bandwidth of video.
 */
export function VideoBand({
  src,
  poster,
  webm,
  eager = false,
  scrim = 0.5,
  rate = 0.6,
  surround = "#faf7f1",
  minHeight = 520,
  crossfade = true,
  withSound = false,
  soundCaption = null,
  className = "",
  children,
  testId = "video-band",
}) {
  const ref = useRef(null);
  const aRef = useRef(null);
  const bRef = useRef(null);
  const rafRef = useRef(0);
  const reduce = useReducedMotion();
  const near = useInView(ref, { margin: "500px 0px 500px 0px" });
  const onScreen = useInView(ref, { amount: 0.12 });
  const [armed, setArmed] = useState(eager);
  const [ready, setReady] = useState(false);
  const [muted, setMuted] = useState(true);
  const [small, setSmall] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setSmall(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (near) setArmed(true);
  }, [near]);

  const live = !reduce && !small && armed;

  const prime = useCallback(
    (v, offsetHalf) => {
      if (!v) return;
      v.playbackRate = rate;
      const d = v.duration;
      if (offsetHalf && Number.isFinite(d) && d > 1) {
        try { v.currentTime = d / 2; } catch (e) { /* seek not ready yet */ }
      }
    },
    [rate],
  );

  /* Opacity is driven per frame: each element fades through its own seam while
     the other is mid-clip, so the loop point is never visible. */
  useEffect(() => {
    if (!live || !onScreen) {
      cancelAnimationFrame(rafRef.current);
      [aRef.current, bRef.current].forEach((v) => v && v.pause());
      return undefined;
    }
    [aRef.current, bRef.current].forEach((v) => {
      if (!v) return;
      v.playbackRate = rate;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    });

    // When sound is enabled we ship a single track (no crossfade twin) so
    // audio is never doubled. Opacity stays at 1 for the primary video.
    if (!crossfade || withSound) return undefined;

    const tick = () => {
      [aRef.current, bRef.current].forEach((v) => {
        if (!v || !Number.isFinite(v.duration)) return;
        const t = v.currentTime;
        const d = v.duration;
        const alpha = Math.max(0, Math.min(1, Math.min(t / FADE, (d - t) / FADE)));
        v.style.opacity = String(alpha);
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [live, onScreen, crossfade, rate, withSound]);

  const toggleSound = useCallback(() => {
    const v = aRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    if (!next && v.paused) v.play().catch(() => {});
    setMuted(next);
  }, []);

  const videoCls = "absolute inset-0 h-full w-full object-cover";
  const driftStyle = reduce
    ? undefined
    : { animation: "vb-drift 32s ease-in-out infinite alternate", willChange: "transform" };
  /* Mobile ships the poster only — the same drift keeps it feeling live. */
  const posterDrift = !reduce && small ? driftStyle : undefined;

  const sources = (
    <>
      {webm && <source src={webm} type="video/webm" />}
      <source src={src} type="video/mp4" />
    </>
  );

  const bandMin = small && typeof minHeight === "number" ? Math.round(minHeight * 0.78) : minHeight;

  return (
    <section
      ref={ref}
      data-testid={testId}
      className={`force-dark relative isolate overflow-hidden ${className}`}
      style={{ backgroundColor: "#0B0906", minHeight: bandMin }}
    >
      {/* Designed still — a rich layered gradient ground that sits UNDER the
          poster and video. When the video fails, the hero reads as an
          intentional composition of brand deep tones rather than an empty
          template. Two conic-radial washes plus a subtle grain (below). */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 30% 25%, rgba(138,106,47,0.28) 0%, transparent 60%),"
            + " radial-gradient(70% 60% at 78% 82%, rgba(192,97,42,0.22) 0%, transparent 60%),"
            + " linear-gradient(180deg, #100b06 0%, #0b0906 55%, #050403 100%)",
        }}
      />

      {/* Poster: painted immediately, never removed. Use img alone — the
          webp source was previously served by the SPA catch-all as text/html,
          giving a broken-image glyph on every hero. */}
      <div aria-hidden="true" data-testid={`${testId}-poster`} className="absolute inset-0 overflow-hidden">
        <img
          src={poster}
          alt=""
          decoding="async"
          fetchPriority={eager ? "high" : "auto"}
          className="h-full w-full object-cover"
          style={posterDrift}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>

      {live && (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            opacity: ready ? 1 : 0,
            transition: "opacity 1200ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <video
            ref={aRef}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            preload={eager ? "auto" : "metadata"}
            aria-hidden={withSound ? undefined : "true"}
            data-testid={`${testId}-video`}
            className={videoCls}
            style={driftStyle}
            onCanPlay={(e) => { prime(e.currentTarget, false); setReady(true); }}
          >
            {sources}
          </video>

          {crossfade && !withSound && (
            <video
              ref={bRef}
              autoPlay
              muted
              loop
              playsInline
              disablePictureInPicture
              preload={eager ? "auto" : "metadata"}
              aria-hidden="true"
              className={videoCls}
              style={{ ...driftStyle, opacity: 0 }}
              onLoadedMetadata={(e) => prime(e.currentTarget, true)}
            >
              {sources}
            </video>
          )}
        </div>
      )}

      {/* Film grain — also kills gradient banding on cheap panels. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: GRAIN, opacity: 0.04, mixBlendMode: "overlay" }}
      />
      {/* Vignette. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse at center, rgba(11,9,6,0) 35%, rgba(11,9,6,0.55) 100%)" }}
      />
      {/* Surround blend intentionally removed — the light gradient at the video
          edges was washing the footage out and reading as an off-white overlay.
          The band now abuts the surrounding page cleanly via border-y/border-b. */}
      {/* Scrim, tuned per clip so every headline clears AA. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ backgroundColor: `rgba(11, 9, 6, ${scrim})` }}
      />

      {/* Sound control — only when withSound is on AND a <video> is actually
          mounted (live=false under prefers-reduced-motion, so the button
          would be a decorative no-op). Anchored top-right of the hero. */}
      {withSound && live && (
        <>
          <button
            type="button"
            onClick={toggleSound}
            data-testid={`${testId}-sound-toggle`}
            aria-pressed={!muted}
            aria-label={muted ? "Play with sound" : "Mute"}
            title={muted ? "Play with sound" : "Mute"}
            className="film-btn film-btn--sm absolute right-4 top-24 z-20 sm:right-6 sm:top-28"
          >
            <span aria-hidden="true" className="film-btn__icon">
              {muted ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5 6 9H2v6h4l5 4z" />
                  <line x1="22" y1="9" x2="16" y2="15" />
                  <line x1="16" y1="9" x2="22" y2="15" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5 6 9H2v6h4l5 4z" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </span>
          </button>

          {soundCaption && (
            <p
              data-testid={`${testId}-credit`}
              className="ui absolute left-5 z-20 max-w-[var(--measure-read)] text-[10.5px] leading-[1.55] tracking-[0.02em] text-[rgba(243,239,231,0.8)] sm:left-8"
              style={{ top: "calc(100svh - 240px)" }}
            >
              {soundCaption}
            </p>
          )}
        </>
      )}

      <div className="relative z-10">{children}</div>
    </section>
  );
}
